import React, { useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
export const renderToDom = (Element, { strictMode = true, selector = '#root' } = {}) => {
    const root = createRoot(document.querySelector(selector));
    root.render(strictMode ? React.createElement(React.StrictMode, null, Element) : Element);
    return root;
};
/** Use for linter bypass */
export const useMountEffect = (effect) => {
    useEffect(effect, []);
};
export const useToggleState = (
/**
 * @default false
 */
initialState = false) => {
    const [state, setState] = useState(initialState);
    const turnOn = useCallback(() => setState(true), []);
    const turnOff = useCallback(() => setState(false), []);
    const toggle = useCallback((s) => (s !== undefined ? setState(s) : setState(c => !c)), []);
    return {
        state,
        on: turnOn,
        off: turnOff,
        toggle,
    };
};
export const useModalState = (
/**
 * The same `useToggleState` but with property names for modal
 * @default false
 */
initialState = false) => {
    const { state, toggle, off, on } = useToggleState(initialState);
    return {
        isOpen: state,
        open: on,
        close: off,
        toggle,
    };
};
export { default as ErrorBoundary } from './errorBoundary';
export { extendComponent } from './extendComponent';
export const useAwaitedClickAction = (action, forceDisabled = false) => {
    const [disabled, setDisabled] = useState(false);
    return {
        disabled: disabled || forceDisabled,
        async onClick() {
            if (disabled)
                return;
            // if (forceDisabled) return
            setDisabled(true);
            try {
                await action();
            }
            finally {
                setDisabled(false);
            }
        },
    };
};
export const useInitEffect = (cb) => {
    useEffect(() => {
        const result = cb();
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
export const globalValidator = {
    value: (a) => { }
};
export const useAsync = (fn, deps = [], enableQuery = true, usePolling = null) => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!enableQuery) {
            setLoading(false);
            setError('');
        }
        const abortController = new AbortController();
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await fn(abortController.signal);
                globalValidator.value(res);
                if (abortController.signal.aborted)
                    return;
                setData(res);
            }
            catch (err) {
                setError(err.message);
            }
            finally {
                setLoading(false);
            }
        };
        fetchData();
        const interval = usePolling ? setInterval(fetchData, usePolling) : null;
        return () => {
            abortController.abort();
            if (interval)
                clearInterval(interval);
        };
    }, deps);
    return {
        error,
        loading,
        data,
    };
};
export { default as useUtilsEffect } from './useUtilsEffect';
